import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useGetInterestsQuery, useDeleteInterestMutation, useToggleInterestStatusMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';
import { confirmAction } from '../../../utils/confirm';

const AdminInterests: React.FC = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const { data, isLoading, error } = useGetInterestsQuery({ page });
  const [deleteInterest] = useDeleteInterestMutation();
  const [toggleStatus] = useToggleInterestStatusMutation();

  const handleDelete = async (id: number) => {
    const confirmed = await confirmAction(
      'Delete Interest',
      'Are you sure you want to delete this interest?',
      'Yes, Delete'
    );
    if (confirmed) {
      try {
        await deleteInterest(id).unwrap();
        showSuccess('Interest deleted successfully');
      } catch (err: any) {
        showError(err?.data?.message || 'Failed to delete interest');
      }
    }
  };

  const handleToggleStatus = async (id: number, currentStatus: number) => {
    try {
      const newStatus = currentStatus ;
      await toggleStatus({ id, status: newStatus }).unwrap();
      showSuccess(`Interest ${!newStatus ? 'activated' : 'deactivated'} successfully`);
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to update status');
    }
  };

  if (isLoading) {
    return <PageLoader />;
  }

  if (error) {
    return <div className="alert alert-danger m-5">Error loading interests</div>;
  }

  const interests = data?.data || { data: [], current_page: 1, last_page: 1 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Interests</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted">
                  <Link to="/admin" className="text-muted text-hover-primary">Home</Link>
                </li>
                <li className="breadcrumb-item">
                  <span className="bullet bg-gray-400 w-5px h-2px"></span>
                </li>
                <li className="breadcrumb-item text-muted">Interests</li>
              </ul>
            </div>
            <div className="d-flex align-items-center gap-2 gap-lg-3">
              <button
                onClick={() => navigate('/admin/interests/add')}
                className="btn btn-sm fw-bold btn-primary"
              >
                Add Interest
              </button>
            </div>
          </div>
        </div>
        
        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-body py-4">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                        <th className="min-w-50px">ID</th>
                        <th className="min-w-125px">Name</th>
                        <th className="min-w-125px">Name (AR)</th>
                        <th className="min-w-100px">Status</th>
                        <th className="text-end min-w-100px">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="text-gray-600 fw-semibold">
                      {interests.data.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center">No interests found</td>
                        </tr>
                      ) : (
                        interests.data.map((interest: any) => (
                          <tr key={interest.id}>
                            <td>{interest.id}</td>
                            <td>{interest.title}</td>
                            <td>{interest.title_ar}</td>
                            <td>
                              <div className="form-check form-switch form-check-custom form-check-solid">
                                <input
                                  className="form-check-input"
                                  type="checkbox"
                                  checked={interest.status}
                                  onChange={() => handleToggleStatus(interest.id, interest.status)}
                                />
                              </div>
                            </td>
                            <td className="text-end">
                              <ActionButtons
                                onEdit={() => navigate(`/admin/interests/${interest.id}/edit`)}
                                onDelete={() => handleDelete(interest.id)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {interests.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={interests.current_page}
                      lastPage={interests.last_page}
                      total={interests.total || 0}
                      perPage={interests.per_page || 15}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInterests;
